import { useState, useEffect } from "react";
import { useMatchingConfig, type MatchingConfigRow } from "@/hooks/useMatchingConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { HelpCircle, Save, RotateCcw } from "lucide-react";

type UpdatePatch = Partial<
  Pick<
    MatchingConfigRow,
    | "containment_auto_threshold"
    | "containment_borderline_threshold"
    | "require_entity_match"
    | "require_frame_match_for_auto"
    | "send_weak_to_llm"
    | "llm_batch_size"
    | "llm_concurrency"
    | "borderline_quality_as"
  >
>;

function InfoTooltip({ text }: { text: string }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <HelpCircle className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground cursor-help shrink-0" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function MatchingConfigPanel() {
  const { config, update } = useMatchingConfig();
  const { toast } = useToast();
  const [draft, setDraft] = useState<UpdatePatch>({});

  // Reset draft gdy config sie zaladuje
  useEffect(() => {
    setDraft({});
  }, [config.data?.id]);

  const current: MatchingConfigRow | null = config.data ?? null;

  const field = <K extends keyof UpdatePatch>(key: K): NonNullable<UpdatePatch[K]> => {
    return (draft[key] ?? current?.[key]) as NonNullable<UpdatePatch[K]>;
  };

  const isDirty = Object.keys(draft).length > 0;

  const save = async () => {
    if (!isDirty) return;
    try {
      await update.mutateAsync(draft);
      setDraft({});
      toast({ title: "Zapisano", description: "Konfiguracja matchera zaktualizowana" });
    } catch (err) {
      toast({
        title: "Błąd zapisu",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    }
  };

  const reset = () => setDraft({});

  if (config.isLoading) {
    return <p className="text-sm text-muted-foreground py-4">Ładowanie konfiguracji...</p>;
  }

  if (!current) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        Brak rekordu globalnego w <code>matching_config</code>. Uruchom migrację M9.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm text-muted-foreground">
            Progi matchera i parametry LLM dla kroku <strong>Istniejące wpisy</strong>.
            Zmiany są globalne — obowiązują wszystkie mapy.
          </p>
        </div>
        {isDirty && (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={reset}>
              <RotateCcw className="h-3.5 w-3.5 mr-1" />
              Cofnij
            </Button>
            <Button size="sm" onClick={save} disabled={update.isPending}>
              <Save className="h-3.5 w-3.5 mr-1" />
              {update.isPending ? "Zapisywanie..." : "Zapisz"}
            </Button>
          </div>
        )}
      </div>

      {/* Containment thresholds */}
      <section className="border rounded-lg p-4 space-y-5">
        <h4 className="text-sm font-semibold flex items-center gap-2">
          Level 2 — Containment thresholds
          <Badge variant="outline" className="text-[10px]">darmowe</Badge>
        </h4>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Auto threshold</label>
            <InfoTooltip text="Node zostaje automatycznie zmatchowany (matched_keep lub matched_refurbish) gdy containment (tokensNody ∩ tokensWpisu) / |tokensNody| >= tego progu." />
            <span className="ml-auto text-sm font-mono tabular-nums">
              {field("containment_auto_threshold").toFixed(2)}
            </span>
          </div>
          <Slider
            min={0.5}
            max={0.95}
            step={0.05}
            value={[field("containment_auto_threshold")]}
            onValueChange={(v) => setDraft((d) => ({ ...d, containment_auto_threshold: v[0] }))}
          />
          <p className="text-[10px] text-muted-foreground">
            Zalecane: 0.70 dla dziedziny domenowej (sushi / kulinaria), 0.80 dla ogólnej.
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Borderline threshold</label>
            <InfoTooltip text="Poniżej auto, powyżej borderline → node trafia do Level 3 (LLM wp_matching) w 'Matching głęboki'. Jeśli mode=fast → node zostanie unmatched." />
            <span className="ml-auto text-sm font-mono tabular-nums">
              {field("containment_borderline_threshold").toFixed(2)}
            </span>
          </div>
          <Slider
            min={0.2}
            max={0.7}
            step={0.05}
            value={[field("containment_borderline_threshold")]}
            onValueChange={(v) =>
              setDraft((d) => ({ ...d, containment_borderline_threshold: v[0] }))
            }
          />
        </div>

        <div className="grid grid-cols-2 gap-4 pt-2">
          <div className="flex items-start gap-2">
            <Switch
              checked={field("require_entity_match")}
              onCheckedChange={(v) => setDraft((d) => ({ ...d, require_entity_match: v }))}
            />
            <div className="flex-1">
              <div className="text-sm font-medium flex items-center gap-1">
                Require entity match
                <InfoTooltip text="Auto-match wymaga żeby primary_entity nody występowało w content_plaintext wpisu (word-boundary match po strip diacritics)." />
              </div>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Switch
              checked={field("require_frame_match_for_auto")}
              onCheckedChange={(v) =>
                setDraft((d) => ({ ...d, require_frame_match_for_auto: v }))
              }
            />
            <div className="flex-1">
              <div className="text-sm font-medium flex items-center gap-1">
                Require frame match (auto)
                <InfoTooltip text="Auto-match wymaga żeby query_frame nody (np. procedural 'jak zrobić X') zgadzał się z query_frame wpisu. Wymaga wcześniejszej ekstrakcji SRO." />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LLM L3 */}
      <section className="border rounded-lg p-4 space-y-4">
        <h4 className="text-sm font-semibold flex items-center gap-2">
          Level 3 — LLM wp_matching
          <Badge variant="outline" className="text-[10px]">OpenAI gpt-4.1-mini</Badge>
        </h4>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Batch size</label>
              <InfoTooltip text="Ile par node↔wpis wysyłamy do LLM w jednym requestcie. Większy = mniej calls, ale więcej tokenów per call. Zalecane: 10." />
            </div>
            <Input
              type="number"
              min={5}
              max={30}
              value={field("llm_batch_size")}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  llm_batch_size: Math.max(5, Math.min(30, parseInt(e.target.value) || 10)),
                }))
              }
            />
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Concurrency</label>
              <InfoTooltip text="Ile batchy leci równolegle. 3 = ~3× szybciej. Uwaga na rate limity OpenAI — max 10." />
            </div>
            <Input
              type="number"
              min={1}
              max={10}
              value={field("llm_concurrency")}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  llm_concurrency: Math.max(1, Math.min(10, parseInt(e.target.value) || 3)),
                }))
              }
            />
          </div>
        </div>
      </section>

      {/* Borderline handling */}
      <section className="border rounded-lg p-4 space-y-3">
        <h4 className="text-sm font-semibold">Polityka dla niskiej jakości wpisów</h4>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">
              Wpis borderline quality (score 45-64) traktuj jako
            </label>
            <InfoTooltip text="Gdy containment wysoki ale quality_recommendation='borderline' — decyduje czy zachować (matched_keep) czy od razu do przebudowy (matched_refurbish)." />
          </div>
          <Select
            value={field("borderline_quality_as")}
            onValueChange={(v) =>
              setDraft((d) => ({
                ...d,
                borderline_quality_as: v as UpdatePatch["borderline_quality_as"],
              }))
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="matched_keep">matched_keep (zachowaj mimo wszystko)</SelectItem>
              <SelectItem value="matched_refurbish">matched_refurbish (do przebudowy)</SelectItem>
              <SelectItem value="partial">partial (ręczna decyzja)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </section>
    </div>
  );
}
